import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {BehaviorSubject, defer, Observable, throwError} from 'rxjs';
import {catchError, finalize, switchMap} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {AbstractHttpService} from './abstract-http-service';
import {OfferRegistryProcessStatusInterface} from '../model/offer-registry-process-status.interface';

const duplicateQueueStatusCode = 409;

export interface OfferRegistryQueueProcessResponse {
  message: string;
  commandId: number;
}

interface OfferRegistryQueueProcessPayload {
  accountId: number;
  offerType?: string | null;
  ruleId?: number | null;
  offerSetIds?: number[] | null;
}

@Injectable({
  providedIn: 'root'
})
export class OfferRegistryProcessorSyncService extends AbstractHttpService {
  private processingGuard = new BehaviorSubject<boolean>(false);
  private processingGuardInFlightCount = 0;

  private options = {
    headers: this.contentTypeJsonHeaders,
    withCredentials: true
  };

  constructor(private httpClient: HttpClient) {
    super();
  }

  /**
   * Calls the private-offer registry sync endpoint so the backend can reconcile
   * `private_offer_data_registry` rows for all active standard offer sets for the given account.
   */
  processForStandardOfferSets(accountId: number, ruleId: number | null | undefined): Observable<void> {
    const requestUrl = `//${environment.autoDomain}:${environment.autoPort}/api/offers-registry/process`;
    const payload: {accountId: number; ruleId?: number} = {
      accountId
    };

    if (ruleId !== null && ruleId !== undefined) {
      payload.ruleId = ruleId;
    }

    const response = this.httpClient.post<void>(requestUrl,
      payload,
      this.options
    ).pipe(
      catchError(this.handleError)
    );

    return response;
  }

  queueProcessForStandardOfferSets(accountId: number, ruleId: number | null | undefined): Observable<OfferRegistryQueueProcessResponse> {
    const requestUrl = `//${environment.autoDomain}:${environment.autoPort}/api/offers-registry/queue-process`;
    const payload: OfferRegistryQueueProcessPayload = {
      accountId
    };

    if (ruleId !== null && ruleId !== undefined) {
      payload.ruleId = ruleId;
    }

    const response = this.httpClient.post<OfferRegistryQueueProcessResponse>(requestUrl,
      payload,
      this.options
    ).pipe(
      catchError(this.handleError)
    );

    return response;
  }

  /**
   * Gets account-level offer registry process status for the selected account.
   */
  getProcessStatus(accountId: number): Observable<OfferRegistryProcessStatusInterface> {
    const requestUrl = `//${environment.autoDomain}:${environment.autoPort}/api/offers-registry/process-status?accountId=${accountId}`;
    const response = this.httpClient.get<OfferRegistryProcessStatusInterface>(
      requestUrl,
      this.options
    ).pipe(
      catchError(this.handleError)
    );

    return response;
  }

  /**
   * Queues account-level offer registry processing for all rules in the selected account.
   */
  queueProcessForAllAccountRules(accountId: number): Observable<OfferRegistryQueueProcessResponse> {
    const requestUrl = `//${environment.autoDomain}:${environment.autoPort}/api/offers-registry/queue-process`;
    const payload: OfferRegistryQueueProcessPayload = {
      accountId,
      offerType: null,
      ruleId: null,
      offerSetIds: null
    };
    const response = this.httpClient.post<OfferRegistryQueueProcessResponse>(
      requestUrl,
      payload,
      this.options
    ).pipe(
      catchError((error: HttpErrorResponse) => this.handleQueueProcessError(error))
    );

    return response;
  }

  /**
   * Preserves duplicate queue responses for component-level in-progress handling.
   */
  private handleQueueProcessError(error: HttpErrorResponse): Observable<never> {
    let handledError = this.handleError(error);

    if (error.status === duplicateQueueStatusCode) {
      handledError = throwError(error);
    }

    return handledError;
  }

  /**
   * Calls the private-offer sync endpoint so the backend can sync
   * `private_offer_data_registry` rows for all active standard offer sets for the given account.
   */
  syncForAllStandardOfferSets(accountId: number): Observable<void> {
    const requestUrl = `//${environment.autoDomain}:${environment.autoPort}/api/offers-registry/sync`;
    const response = this.httpClient.post<void>(requestUrl,
      {
        accountId
      },
      this.options
    ).pipe(
      catchError(this.handleError)
    );

    return response;
  }

  /**
   * Calls the private-offer registry process and sync endpoints so the backend can reconcile
   * `private_offer_data_registry` rows for all active standard offer sets for the given account.
   */
  processAndSyncForStandardOfferSets(accountId: number, ruleId: number | null | undefined): Observable<void> {
    const processAndSync = defer(() => {
      this.incrementProcessingGuard();

      return this.processForStandardOfferSets(accountId, ruleId).pipe(
        switchMap(() => this.syncForAllStandardOfferSets(accountId)),
        finalize(() => {
          this.decrementProcessingGuard();
        })
      );
    });

    return processAndSync;
  }

  /**
   * Increments the processing guard count. If the guard count is 0, it will set the guard to true.
   * If the guard count is greater than 0, it will not set the guard to true.
   */
  private incrementProcessingGuard(): void {
    this.processingGuardInFlightCount = this.processingGuardInFlightCount + 1;

    if (this.processingGuardInFlightCount > 0) {
      this.processingGuard.next(true);
    }
  }

  /**
   * Decrements the processing guard count. If the guard count is 0, it will set the guard to true.
   * If the guard count is greater than 0, it will not set the guard to true.
   */
  private decrementProcessingGuard(): void {
    if (this.processingGuardInFlightCount > 0) {
      this.processingGuardInFlightCount = this.processingGuardInFlightCount - 1;
    }

    if (this.processingGuardInFlightCount === 0) {
      this.processingGuard.next(false);
    }
  }

  /**
   * Returns true if the processing guard is active.
   */
  isProcessingGuardActive(): boolean {
    return this.processingGuard.value;
  }
}
